import { Box, Button, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { saveAs } from "file-saver";

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov', 'm4v', 'avi', 'mkv', 'flv', 'wmv', 'm3u8'];
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

const getFileExtension = (documentDetails) => {
    const source = `${documentDetails?.title || ''} ${documentDetails?.url || ''}`.toLowerCase();
    const match = source.match(/\.([a-z0-9]+)(?:$|\?|#|\s)/);
    return match ? match[1] : '';
};

const getPreviewType = (extension) => {
    if (extension === 'pdf') return 'pdf';
    if (extension === 'txt') return 'text';
    if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
    if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
    if (OFFICE_EXTENSIONS.includes(extension)) return 'office';
    return 'unknown';
};

const DocPreview = ({ documentDetails }) => {
    const [documentBlob, setDocumentBlob] = useState(null);
    const [textPreview, setTextPreview] = useState('');
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState('');
    const [officeViewer, setOfficeViewer] = useState('microsoft');

    const extension = useMemo(() => getFileExtension(documentDetails), [documentDetails]);
    const previewType = useMemo(() => getPreviewType(extension), [extension]);

    const previewUrl = useMemo(() => {
        if (!documentBlob) return '';
        return URL.createObjectURL(documentBlob);
    }, [documentBlob]);

    const officeMicrosoftPreviewUrl = useMemo(() => {
        if (previewType !== 'office' || !documentDetails?.url) return '';
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(documentDetails.url)}`;
    }, [documentDetails?.url, previewType]);

    const officeGooglePreviewUrl = useMemo(() => {
        if (previewType !== 'office' || !documentDetails?.url) return '';
        return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(documentDetails.url)}`;
    }, [documentDetails?.url, previewType]);

    const officePreviewUrl = useMemo(() => {
        if (officeViewer === 'google') return officeGooglePreviewUrl;
        return officeMicrosoftPreviewUrl;
    }, [officeGooglePreviewUrl, officeMicrosoftPreviewUrl, officeViewer]);

    const buildPreviewBlob = async (response, type) => {
        const fileBuffer = await response.arrayBuffer();
        const bytes = new Uint8Array(fileBuffer);
        const isPdfSignature =
            bytes.length >= 5 &&
            bytes[0] === 0x25 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x44 &&
            bytes[3] === 0x46 &&
            bytes[4] === 0x2D;

        const contentType = response.headers.get('content-type') || '';
        const hasPdfContentType = contentType.toLowerCase().includes('application/pdf');

        if (type === 'pdf' || isPdfSignature || hasPdfContentType) {
            return new Blob([fileBuffer], { type: 'application/pdf' });
        }
        if (type === 'image' && extension === 'svg') {
            return new Blob([fileBuffer], { type: 'image/svg+xml' });
        }
        if (type === 'text') {
            return new Blob([fileBuffer], { type: 'text/plain' });
        }

        return new Blob([fileBuffer], { type: contentType || 'application/octet-stream' });
    };

    useEffect(() => {
        if (!documentDetails?.url) {
            setDocumentBlob(null);
            setTextPreview('');
            setPreviewError('');
            setOfficeViewer('microsoft');
            return;
        }
        if (previewType === 'office' || previewType === 'unknown') {
            setDocumentBlob(null);
            setTextPreview('');
            setIsLoadingPreview(false);
            setPreviewError('');
            setOfficeViewer('microsoft');
            return;
        }

        const controller = new AbortController();
        setIsLoadingPreview(true);
        setPreviewError('');
        setTextPreview('');

        fetch(documentDetails.url, {
            headers: { "Cache-Control": "no-cache" },
            signal: controller.signal
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Unable to load document preview');
                }
                if (previewType === 'text') {
                    return response.text().then((text) => {
                        setTextPreview(text);
                        setDocumentBlob(new Blob([text], { type: 'text/plain' }));
                        return null;
                    });
                }
                return buildPreviewBlob(response, previewType);
            })
            .then((blob) => {
                if (blob) {
                    setDocumentBlob(blob);
                }
            })
            .catch((error) => {
                if (error.name === 'AbortError') return;
                console.error("Error loading the preview:", error);
                setPreviewError('Preview is unavailable. You can still download the file.');
                setDocumentBlob(null);
                setTextPreview('');
            })
            .finally(() => {
                setIsLoadingPreview(false);
            });

        return () => {
            controller.abort();
        };
    }, [documentDetails?.url, previewType, extension]);

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleDownloadDocument = () => {
        if (documentBlob) {
            saveAs(documentBlob, documentDetails?.title);
            return;
        }

        fetch(documentDetails?.url, {
            headers: { "Cache-Control": "no-cache" }

        })
            .then((response) => response.blob()) // Convert the response to a Blob
            .then((blob) => {
                // Use the file-saver library to trigger the download
                saveAs(blob, documentDetails?.title);
            })
            .catch((error) => {
                console.error("Error downloading the file:", error);
            });
    }

    return (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'80vh'}>
            <Box width={'700px'}>
                <Typography fontSize={'20px'} fontWeight={700} mb={1}>Document Preview</Typography>
                {isLoadingPreview && (
                    <Typography fontSize={'14px'} mb={1}>
                        Loading preview...
                    </Typography>
                )}
                {previewError && (
                    <Typography fontSize={'14px'} color={'error.main'} mb={1}>
                        {previewError}
                    </Typography>
                )}
                {previewType === 'pdf' && previewUrl && (
                    <object
                        data={`${previewUrl}#toolbar=0`}
                        type="application/pdf"
                        width="100%"
                        height="600"
                        style={{ border: '1px dotted', borderRadius: '8px' }}
                    >
                        <iframe
                            title="Document Preview"
                            src={`${previewUrl}#toolbar=0`}
                            width="100%"
                            height="600"
                            style={{ border: 0 }}
                        />
                    </object>
                )}
                {previewType === 'image' && previewUrl && (
                    <Box
                        sx={{
                            height: 600,
                            border: '1px dotted',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: '#f7f7f7'
                        }}
                    >
                        <img
                            src={previewUrl}
                            alt={documentDetails?.title || 'preview'}
                            style={{ maxWidth: '100%', maxHeight: '100%' }}
                        />
                    </Box>
                )}
                {previewType === 'video' && previewUrl && (
                    <Box sx={{ border: '1px dotted', borderRadius: '8px', p: 1 }}>
                        <video controls style={{ width: '100%', maxHeight: 580 }}>
                            <source src={previewUrl} />
                            Your browser does not support video preview.
                        </video>
                    </Box>
                )}
                {previewType === 'text' && (
                    <Box
                        sx={{
                            height: 600,
                            border: '1px dotted',
                            borderRadius: '8px',
                            p: 2,
                            overflow: 'auto',
                            bgcolor: '#fff'
                        }}
                    >
                        <Typography component={'pre'} sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            {textPreview || 'No text content available for preview.'}
                        </Typography>
                    </Box>
                )}
                {previewType === 'office' && officePreviewUrl && (
                    <Box>
                        <Box display={'flex'} gap={1} mb={1}>
                            <Button
                                variant={officeViewer === 'microsoft' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setOfficeViewer('microsoft')}
                            >
                                Microsoft Viewer
                            </Button>
                            <Button
                                variant={officeViewer === 'google' ? 'contained' : 'outlined'}
                                size="small"
                                onClick={() => setOfficeViewer('google')}
                            >
                                Google Viewer
                            </Button>
                        </Box>
                        <iframe
                            title="Office Document Preview"
                            key={officeViewer}
                            src={officePreviewUrl}
                            width="100%"
                            height="600"
                            style={{ border: '1px dotted', borderRadius: '8px' }}
                        />
                        <Typography fontSize={'12px'} color={'text.secondary'} mt={0.5}>
                            If one viewer does not load due to permissions, try the other one or use Download.
                        </Typography>
                    </Box>
                )}
                {previewType === 'unknown' && !isLoadingPreview && !previewError && (
                    <Box
                        sx={{
                            height: 600,
                            border: '1px dotted',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 2
                        }}
                    >
                        <Typography fontSize={'14px'} color={'text.secondary'}>
                            Preview is not supported for this file type. Please download the file.
                        </Typography>
                    </Box>
                )}

                <Button
                    variant='outlined'
                    onClick={handleDownloadDocument}
                    sx={{ mt: 1 }}
                    disabled={!documentDetails?.url}
                >
                    Download
                </Button>
            </Box>
        </Box>
    )
}

export default DocPreview